import { useState, useEffect, useRef } from 'react';
import { useTranslation } from 'react-i18next';

interface StatProps {
  end: number;
  duration: number;
  suffix?: string;
  decimals?: number;
  label: string;
}

function AnimatedStat({ end, duration, suffix = '', decimals = 0, label }: StatProps) {
  const [count, setCount] = useState(0);
  const [isVisible, setIsVisible] = useState(false);
  const ref = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting && !isVisible) {
          setIsVisible(true);
        }
      },
      { threshold: 0.3 }
    );

    if (ref.current) {
      observer.observe(ref.current);
    }

    return () => {
      if (ref.current) {
        observer.unobserve(ref.current);
      }
    };
  }, [isVisible]);

  useEffect(() => {
    if (!isVisible) return;

    let startTime: number | null = null;
    const startValue = 0;

    const animate = (currentTime: number) => {
      if (!startTime) startTime = currentTime;
      const progress = Math.min((currentTime - startTime) / duration, 1);

      const easeOutQuart = 1 - Math.pow(1 - progress, 4);
      const currentCount = startValue + (end - startValue) * easeOutQuart;

      setCount(currentCount);

      if (progress < 1) {
        requestAnimationFrame(animate);
      } else {
        setCount(end);
      }
    };

    requestAnimationFrame(animate);
  }, [isVisible, end, duration]);

  return (
    <div ref={ref} className="text-center">
      <div className="text-5xl md:text-6xl font-bold text-white mb-3">
        {count.toFixed(decimals)}
        {suffix}
      </div>
      <div className="text-lg text-gray-200">{label}</div>
    </div>
  );
}

export default function AnimatedStats() {
  const { t } = useTranslation();

  const stats = [
    { end: 8.21, duration: 2500, suffix: ` MWh/${t('by_numbers_year')}`, decimals: 2, label: t('by_numbers_solar_generated') },
    { end: 10000, duration: 2500, suffix: ' L', decimals: 0, label: t('by_numbers_rainwater') },
    { end: 505, duration: 2000, suffix: ' m²', decimals: 0, label: t('by_numbers_living_space') },
    { end: 1190, duration: 2500, suffix: ' m²', decimals: 0, label: t('by_numbers_garden') },
    { end: 4, duration: 1500, suffix: '', decimals: 0, label: t('by_numbers_bedrooms') },
    { end: 5, duration: 1500, suffix: '', decimals: 0, label: t('by_numbers_bathrooms') },
    { end: 4, duration: 1500, suffix: '', decimals: 0, label: t('by_numbers_garage') },
    { end: 20, duration: 2000, suffix: ' kW', decimals: 0, label: t('by_numbers_solar_capacity') },
    { end: 40, duration: 2000, suffix: ' kWh', decimals: 0, label: t('by_numbers_battery_capacity') }
  ];

  return (
    <section className="py-24 px-4 bg-gradient-to-br from-gray-900 to-gray-800 text-white">
      <div className="max-w-7xl mx-auto">
        <h2 className="text-4xl md:text-5xl font-bold text-center mb-4 text-white">
          {t('by_numbers_title')}
        </h2>
        <p className="text-center text-gray-200 mb-16 text-lg">
          {t('by_numbers_subtitle')}
        </p>

        <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-3 gap-12 md:gap-16">
          {stats.map((stat, index) => (
            <AnimatedStat key={index} {...stat} />
          ))}
        </div>
      </div>
    </section>
  );
}
